// src/pages/ImportPatientsPage.jsx
// Route: /patients/import — ADMIN / HEALTH_NAVIGATOR only.
//
// Bulk patient import from an .xlsx spreadsheet. Follows the same
// upload-zone pattern as PatientUploadPage.jsx (var(--token) colors only,
// drag/drop + hidden file input) and the same role-gate fallback UsersPage.js
// uses for its ADMIN-only screen.

import React, { useState, useRef } from 'react';
import { AppShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { patientAPI } from '../services/api';
import Icon from '../components/Icon';

const MAX_FILE_MB = 20;
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function isXlsxFile(f) {
  if (!f) return false;
  if (f.type === XLSX_MIME) return true;
  return /\.xlsx$/i.test(f.name || '');
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImportPatientsPage() {
  const { user } = useAuth();
  const isAllowed = ['ADMIN', 'HEALTH_NAVIGATOR'].includes(user?.role);

  const fileInputRef = useRef(null);

  const [file,      setFile]      = useState(null);
  const [dragOver,  setDragOver]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [result,    setResult]    = useState(null); // { created, updated, skipped, errors }

  if (!isAllowed) {
    return (
      <AppShell breadcrumbs={[{ label: 'Patients', to: '/patients' }, { label: 'Import Patients' }]}>
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="lock" size={20} /></div>
          <div className="empty-title">Access Required</div>
          <div className="empty-desc">You need admin or health navigator privileges to import patients.</div>
        </div>
      </AppShell>
    );
  }

  function handleFileSelect(f) {
    if (!f) return;
    if (!isXlsxFile(f)) {
      setError('Please select an .xlsx file — other formats are not supported.');
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`File too large — maximum ${MAX_FILE_MB}MB`);
      setFile(null);
      return;
    }
    setFile(f);
    setError('');
    setResult(null);
  }

  async function handleImport() {
    if (!file) { setError('Please select a file first.'); return; }
    setError('');
    setUploading(true);
    setResult(null);
    try {
      const res = await patientAPI.importXlsx(file);
      setResult(res.data);
      setFile(null);
    } catch (e) {
      setError(e.response?.data?.message || 'Import failed — please try again.');
    } finally {
      setUploading(false);
    }
  }

  const hasResult = !!result;
  const errors = (hasResult && Array.isArray(result.errors)) ? result.errors : [];
  const hasErrors = errors.length > 0;

  return (
    <AppShell breadcrumbs={[{ label: 'Patients', to: '/patients' }, { label: 'Import Patients' }]}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Import Patients</div>
          <div className="page-subtitle">Bulk-create or update patients from an .xlsx spreadsheet</div>
        </div>
      </div>

      {error && <div className="alert alert-error"><Icon name="alert" size={14} /> {error}</div>}

      <div className="card fade-in-up delay-1">
        <div
          role="button"
          tabIndex={0}
          style={{
            border: `2px dashed ${dragOver ? 'var(--brand-accent)' : file ? 'var(--green)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-md)',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            background: dragOver ? 'var(--bg-subtle)' : file ? 'var(--green-bg)' : 'var(--bg-subtle)',
            transition: 'all 0.15s',
            marginBottom: 20,
            outline: 'none',
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onKeyDown={e => e.key === 'Enter' && !uploading && fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            if (!uploading) handleFileSelect(e.dataTransfer.files[0]);
          }}
        >
          {file ? (
            <>
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="chart" size={30} /></div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--green)', marginBottom: 4 }}>
                {file.name}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {formatSize(file.size)}
              </div>
              {!uploading && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Click to change file
                </div>
              )}
            </>
          ) : (
            <>
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="paperclip" size={30} /></div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                Click to select a spreadsheet
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                or drag and drop here
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                .xlsx only — up to {MAX_FILE_MB}MB
              </div>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: 'none' }}
          onChange={e => handleFileSelect(e.target.files[0])}
        />

        <button
          type="button"
          className="btn btn-primary"
          disabled={!file || uploading}
          onClick={handleImport}
        >
          {uploading ? 'Importing…' : 'Import'}
        </button>
      </div>

      {hasResult && (
        <div className="fade-in-up delay-1">
          <div className="stats-row" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 20 }}>
            <div className="stat-card">
              <div className="stat-label">Created</div>
              <div className="stat-value">{result.created ?? 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Updated</div>
              <div className="stat-value">{result.updated ?? 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Skipped</div>
              <div className="stat-value" style={result.skipped > 0 ? { color: 'var(--red)' } : undefined}>{result.skipped ?? 0}</div>
            </div>
          </div>

          {hasErrors ? (
            <div className="card" style={{ marginTop: 20 }}>
              <div className="page-subtitle" style={{ marginBottom: 12 }}>
                Row errors ({errors.length})
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={`${e.row}-${i}`}>
                        <td>{e.row}</td>
                        <td>{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="alert alert-success" style={{ marginTop: 20 }}>
              ✓ Import complete — no row errors.
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
